import type { Permission } from '@api/app'
import type { AppAbility } from '@layerit/auth/ability'
import { abilityFromPermissions, subjectAndAction } from '@layerit/auth/ability'
import type { UseQueryResult } from '@tanstack/react-query'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { ApiError } from '@web/api'
import { authClient } from '@web/auth'
import { queryKeys, resetTenantQueries } from '@web/query'
import type { RouteName } from '@web/router'
import { readSignInError, routeNameFor } from '@web/router'
import { rpc, unwrap } from '@web/rpc'

export interface AdminUser {
	id: string
	email: string
	displayName: string | null
	/** A default role's key, or a custom role's. What it may do is `permissions`, not this. */
	role: string
	tenantId: string
}

export type SessionState =
	| { status: 'loading' }
	| { status: 'anonymous'; error: string | null }
	| {
			status: 'organisation'
			user: SignedInUser
			displayName: string | null
			activeOrganizationId: string | null
			reason: string | null
	  }
	| { status: 'ready'; user: AdminUser; permissions: string[] }
	| { status: 'unavailable'; message: string }

export function useRouteName(): RouteName | null {
	const { pathname } = useLocation()
	return routeNameFor(pathname)
}

type SignedInUser = NonNullable<ReturnType<typeof authClient.useSession>['data']>['user']

type SessionUser = Awaited<ReturnType<typeof fetchSession>>

async function fetchSession() {
	return unwrap(await rpc.v1.admin.me.$get())
}

export interface SessionHook {
	state: SessionState
	reload: () => Promise<void>
	signOut: () => Promise<void>

	ability: AppAbility | null

	can: (permission: Permission) => boolean
}

export function useSession(): SessionHook {
	const auth = authClient.useSession()
	const { search } = useLocation()
	const activeOrganizationId = auth.data?.session.activeOrganizationId ?? null
	const signedIn = auth.data !== null && auth.data !== undefined
	const userId = auth.data?.user.id ?? null

	const settled = useRef(false)
	if (!auth.isPending) settled.current = true

	const me = useQuery({
		queryKey: [...queryKeys.session, userId, activeOrganizationId],
		queryFn: fetchSession,
		retry: false,
		enabled: signedIn && activeOrganizationId !== null,
	})

	const queryClient = useQueryClient()

	const account = useRef<string | null>(null)
	useEffect(() => {
		if (auth.isPending) return
		const current = `${userId ?? ''}:${activeOrganizationId ?? ''}`
		const previous = account.current
		account.current = current
		if (previous !== null && previous !== current) void resetTenantQueries(queryClient)
	}, [auth.isPending, userId, activeOrganizationId, queryClient])

	const reload = useCallback(async () => {
		await auth.refetch()
		await queryClient.invalidateQueries({ queryKey: queryKeys.session })
	}, [auth, queryClient])

	const signOut = useCallback(async () => {
		try {
			await authClient.signOut()
		} catch {}

		queryClient.clear()
	}, [queryClient])

	/* Built from the permissions the server resolved, not from the role name: a custom role's
	 * grant is a stored row the console cannot derive, and this is the list the API enforces. */
	const permissions = me.data?.permissions
	const ability = useMemo(() => (permissions !== undefined ? abilityFromPermissions(permissions) : null), [permissions])

	const can = useCallback(
		(permission: Permission) => {
			if (!ability) return false
			const [subject, action] = subjectAndAction(permission)
			return ability.can(action, subject)
		},
		[ability],
	)

	return { state: sessionState(auth, settled.current, me, activeOrganizationId, search), reload, signOut, ability, can }
}

function sessionState(
	auth: ReturnType<typeof authClient.useSession>,
	settled: boolean,
	me: UseQueryResult<SessionUser>,
	activeOrganizationId: string | null,
	search: string,
): SessionState {
	if (auth.isPending && !settled) return { status: 'loading' }

	if (auth.data === null || auth.data === undefined) {
		return { status: 'anonymous', error: readSignInError(search) }
	}

	const user = auth.data.user

	const displayName = user.name.trim() === '' ? null : user.name.trim()

	if (activeOrganizationId === null) {
		return { status: 'organisation', user, displayName, activeOrganizationId: null, reason: null }
	}

	if (me.isPending) return { status: 'loading' }

	if (me.data !== undefined) {
		return { status: 'ready', user: me.data.user, permissions: [...me.data.permissions] }
	}

	const error: unknown = me.error
	if (error instanceof ApiError && error.isUnauthenticated) {
		return {
			status: 'organisation',
			user,
			displayName,
			activeOrganizationId,
			reason: 'Your membership in that organisation could not be confirmed.',
		}
	}

	return {
		status: 'unavailable',
		message: error instanceof Error ? error.message : 'Could not reach the server.',
	}
}
